import { Autocomplete, Chip, Button, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, TextField, Typography, List, ListItem, ListItemText, ListItemSecondaryAction, IconButton, FormControl, InputLabel, Select, MenuItem, Divider } from '@mui/material';
import { Box } from '@mui/system';
import { Delete, Add } from '@mui/icons-material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { closeDialog } from '../../features/dialog';
import { enqueueErrorSnack, enqueueSuccessSnack } from '../../features/snackbar';
import axios from 'axios';
import { commonApiPromise } from '../../common';

const DIALOG_ID = "editGroup"
const DIALOG_WIDTH = "sm"

function EditGroupDialog({ onGroupUpdated }) {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const group = useSelector((state) => state.dialog[DIALOG_ID])
    const allChannels = useSelector(state => state.netfeedr.list);
    const show = !!group;
    const [groupLabel, setGroupLabel] = useState('');
    const [selectedChannelToAdd, setSelectedChannelToAdd] = useState('');
    const [multiChannelsToAdd, setMultiChannelsToAdd] = useState([]);
    const [localGroupChannels, setLocalGroupChannels] = useState([]);

    const handleSubmit = e => {
        e && e.preventDefault() && e.stopPropagation();

        if (!groupLabel.trim()) {
            dispatch(enqueueErrorSnack("Group name is required"));
            return;
        }

        setLoading(true);
        
        commonApiPromise(axios.post("/netfeedr/group_edit", { 
            groupId: group.groupId,
            groupLabel: groupLabel.trim() 
        }), dispatch)
        .then(data => {
            if (data && !data.error) {
                dispatch(closeDialog(DIALOG_ID));
                dispatch(enqueueSuccessSnack("Group updated successfully"));
                // Refresh the groups list
                if (onGroupUpdated) {
                    onGroupUpdated();
                }
            }
        })
        .catch(error => {
            // Error handling is done by commonApiPromise
        })
        .finally(() => {
            setLoading(false);
        });
    }

    const removeChannelFromGroup = (channelId) => {
        setLoading(true);
        commonApiPromise(axios.post("/netfeedr/channel_remove_from_group", {
            channelId: channelId,
            groupId: group.groupId
        }), dispatch)
        .then(data => {
            if (data && !data.error) {
                // Update local state immediately
                setLocalGroupChannels(prev => prev.filter(channel => channel.id !== channelId));
                dispatch(enqueueSuccessSnack("Channel removed from group"));
                if (onGroupUpdated) {
                    onGroupUpdated();
                }
            }
        })
        .finally(() => {
            setLoading(false);
        });
    };

    const addChannelToGroup = () => {
        if (!selectedChannelToAdd) return;
        
        const channelToAdd = (allChannels || []).find(c => c.id === parseInt(selectedChannelToAdd));
        if (!channelToAdd) return;
        
        setLoading(true);
        commonApiPromise(axios.post("/netfeedr/channel_assign_to_group", {
            channelId: parseInt(selectedChannelToAdd),
            groupId: group.groupId
        }), dispatch)
        .then(data => {
            if (data && !data.error) {
                // Update local state immediately
                setLocalGroupChannels(prev => [...prev, channelToAdd]);
                dispatch(enqueueSuccessSnack("Channel added to group"));
                setSelectedChannelToAdd('');
                if (onGroupUpdated) {
                    onGroupUpdated();
                }
            }
        })
        .finally(() => {
            setLoading(false);
        });
    };

    // Multi-channel add
    const addMultiChannelsToGroup = () => {
        if (!multiChannelsToAdd.length) return;
        setLoading(true);
        // Only add channels not already in group
        const newChannels = multiChannelsToAdd.filter(cid => !localGroupChannels.some(gc => gc.id === cid));
        Promise.all(newChannels.map(cid => {
            return commonApiPromise(axios.post("/netfeedr/channel_assign_to_group", {
                channelId: cid,
                groupId: group.groupId
            }), dispatch)
            .then(data => {
                if (data && !data.error) {
                    const channelObj = (allChannels || []).find(c => c.id === cid);
                    if (channelObj) {
                        setLocalGroupChannels(prev => {
                            if (prev.some(ch => ch.id === channelObj.id)) return prev;
                            return [...prev, channelObj];
                        });
                    }
                }
            });
        }))
        .then(() => {
            dispatch(enqueueSuccessSnack("Channels added to group"));
            setMultiChannelsToAdd([]);
            if (onGroupUpdated) {
                onGroupUpdated();
            }
        })
        .finally(() => {
            setLoading(false);
        });
    };

    const onClose = () => {
        dispatch(closeDialog(DIALOG_ID));
        setSelectedChannelToAdd('');
    }
    
    useEffect(() => {
        if (group && show) {
            setGroupLabel(group.groupLabel || '');
            setSelectedChannelToAdd('');
            setMultiChannelsToAdd([]);
            setLocalGroupChannels(group.channels || []);
        }
    }, [group, show])

    return <Dialog
        open={!!show}
        onClose={onClose}
        fullWidth
        maxWidth={DIALOG_WIDTH}
    >
        <form onSubmit={handleSubmit}>
            <DialogTitle>
                Edit Group
            </DialogTitle>
            <DialogContent dividers={false}>
                <Box pt={2} pb={2}>
                    <TextField
                        autoFocus
                        fullWidth
                        required
                        size="small"
                        label="Group Name"
                        placeholder="Enter group name"
                        value={groupLabel}
                        onChange={e => setGroupLabel(e.target.value)}
                    />
                </Box>

                <Divider />

                <Box pt={2}>
                    <Typography variant="h6" gutterBottom>
                        Add Channel to Group
                    </Typography>
                    {/* <Box display="flex" gap={1} alignItems="center" pb={2}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Select Channel</InputLabel>
                            <Select
                                value={selectedChannelToAdd}
                                label="Select Channel"
                                onChange={e => setSelectedChannelToAdd(e.target.value)}
                            >
                                {(allChannels || [])
                                    .filter(channel => 
                                        // Only show channels not already in this group
                                        !localGroupChannels.some(gc => gc.id === channel.id)
                                    )
                                    .map(channel => (
                                        <MenuItem key={channel.id} value={channel.id}>
                                            {channel.channelName} ({channel.platform}) - {channel.channelId}
                                        </MenuItem>
                                    ))
                                }
                            </Select>
                        </FormControl>
                        <Button 
                            variant="outlined"
                            onClick={addChannelToGroup}
                            disabled={!selectedChannelToAdd || loading}
                            startIcon={<Add />}
                        >
                            Add
                        </Button>
                    </Box> */}
                    {/* Multi-select Autocomplete for channels */}
                    <Box display="flex" gap={1} alignItems="center" pb={2}>
                        <Autocomplete
                            fullWidth={true}
                            multiple
                            options={(allChannels || [])
                                .filter(channel => !localGroupChannels.some(gc => gc.id === channel.id))
                            }
                            getOptionLabel={option => `${option.channelName} (${option.platform}) - ${option.channelId}`}
                            value={(allChannels || []).filter(c => multiChannelsToAdd.includes(c.id))}
                            onChange={(e, value) => setMultiChannelsToAdd(value.map(v => v.id))}
                            renderTags={(tagValue, getTagProps) =>
                                tagValue.map((option, index) => (
                                    <Chip
                                        {...getTagProps({ index })}
                                        key={option.id}
                                        label={`${option.channelName} (${option.platform})`}
                                        size="small"
                                    />
                                ))
                            }
                            renderInput={params => (
                                <TextField {...params} variant="outlined" size="small" label="Add multiple channels" placeholder="Select channels" />
                            )}
                        />
                        <Button
                            variant="outlined"
                            onClick={addMultiChannelsToGroup}
                            disabled={!multiChannelsToAdd.length || loading}
                            startIcon={<Add />}
                        >
                            Add
                        </Button>
                    </Box>
                </Box>

                <Divider />

                <Box pt={2} pb={2}>
                    <Typography variant="h6" gutterBottom>
                        Channels in this group ({localGroupChannels.length})
                    </Typography>
                    
                    {localGroupChannels && localGroupChannels.length > 0 ? (
                        <List dense>
                            {localGroupChannels.map((channel) => (
                                <ListItem key={channel.id}>
                                    <ListItemText 
                                        primary={`${channel.channelName} (${channel.platform})`}
                                        secondary={`ID: ${channel.channelId}`}
                                    />
                                    <ListItemSecondaryAction>
                                        <IconButton 
                                            edge="end" 
                                            size="small"
                                            onClick={() => removeChannelFromGroup(channel.id)}
                                            disabled={loading}
                                        >
                                            <Delete />
                                        </IconButton>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            ))}
                        </List>
                    ) : (
                        <Typography variant="body2" color="textSecondary">
                            No channels assigned to this group
                        </Typography>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                {loading && <LinearProgress />}
                {!loading && <Box sx={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <Button sx={{marginRight: 2}} onClick={onClose}>Cancel</Button>
                    <Button variant="contained" color="primary" type="submit">Update Group</Button>
                </Box>}
            </DialogActions>
        </form>
    </Dialog>
}

export default EditGroupDialog;